import { useQuery } from '@tanstack/react-query'
import api, { formatMMK, ADMIN_ROLES } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts'
import { TrendingUp, Wrench, FolderOpen, Handshake, Wallet, Users } from 'lucide-react'

const PIE_COLORS = ['#7F1D1D', '#B91C1C', '#DC2626', '#EF4444', '#F87171', '#FCA5A5', '#FED7AA', '#FDE68A']
const STATUS_COLORS: Record<string, string> = {
  Open: '#EF4444', 'In Progress': '#F59E0B', Resolved: '#10B981', Closed: '#6B7280',
  Won: '#10B981', Lost: '#EF4444', Qualification: '#7F1D1D', 'Need Analysis': '#B91C1C',
  Proposal: '#DC2626', Negotiation: '#F59E0B',
  Pending: '#F59E0B', Approved: '#10B981', Rejected: '#EF4444', Disbursed: '#6366F1',
}

const ENGINEERING_ROLES = ['chief_engineer', 'operation_manager', 'engineer', 'technician', 'procurement', 'storekeeper']

interface AnalyticsData {
  sr: {
    total: number
    by_status: { name: string; value: number }[]
    by_priority: { name: string; value: number }[]
    by_category: { name: string; value: number }[]
    avg_resolution_hours: number
    trend: { month: string; count: number }[]
  }
  projects: {
    total: number
    by_type: { name: string; value: number }[]
    by_status: { name: string; value: number }[]
  }
  deals: {
    total: number
    funnel: { stage: string; count: number; value: number }[]
  }
  finance: {
    br_total: number
    br_approved: number
    br_by_status: { name: string; value: number }[]
    ec_total: number
    ec_approved: number
    ec_by_status: { name: string; value: number }[]
    ac_total: number
    ac_approved: number
  }
  workload: { name: string; open: number; total: number }[]
}

function StatCard({ icon: Icon, label, value, sub, color }: {
  icon: any; label: string; value: string | number; sub?: string; color: string
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-start gap-3">
      <div className={`${color} p-2.5 rounded-lg shrink-0`}>
        <Icon size={20} className="text-white" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-lg font-bold text-slate-900 mt-0.5">{value}</div>
        {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  )
}

function ChartCard({ title, children, className = '' }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-xl border border-slate-200 shadow-sm p-4 ${className}`}>
      <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
      {children}
    </div>
  )
}

const CustomPieTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2">
        <p className="text-sm font-medium text-slate-900">{payload[0].name}</p>
        <p className="text-sm text-slate-600">{payload[0].value}</p>
      </div>
    )
  }
  return null
}

const CustomBarTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2">
        <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
        {payload.map((p: any, i: number) => (
          <p key={i} className="text-sm font-semibold" style={{ color: p.fill || p.stroke }}>
            {p.name}: {typeof p.value === 'number' && p.value > 100000 ? formatMMK(p.value) : p.value}
          </p>
        ))}
      </div>
    )
  }
  return null
}

function formatHours(h: number) {
  if (h < 24) return `${h.toFixed(1)}h`
  const d = Math.floor(h / 24)
  const rem = Math.round(h % 24)
  return rem > 0 ? `${d}d ${rem}h` : `${d}d`
}

export default function Analytics() {
  const { user } = useAuth()
  const isEngineering = ENGINEERING_ROLES.includes(user?.role || '')
  const isFinance = ['super_admin', 'admin', 'finance'].includes(user?.role || '')

  const { data, isLoading, isError } = useQuery<AnalyticsData>({
    queryKey: ['analytics'],
    queryFn: () => api.get('/analytics').then(r => r.data),
    staleTime: 60000,
  })

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-800" />
    </div>
  )
  if (isError || !data) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
      <div className="text-red-600 font-semibold">Something went wrong</div>
      <div className="text-sm text-slate-500">Unable to load analytics data. A database error occurred — please refresh or contact support.</div>
    </div>
  )

  const srStatusColors = data.sr.by_status.map((d, i) => STATUS_COLORS[d.name] || PIE_COLORS[i % PIE_COLORS.length])
  const prioColors = ['#7F1D1D', '#B91C1C', '#DC2626', '#F59E0B']
  const typeColors = PIE_COLORS

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <TrendingUp size={22} className="text-red-700" />
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Analytics</h1>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Wrench} label="Total SRs" value={data.sr.total}
          sub={`Avg ${formatHours(data.sr.avg_resolution_hours)} resolution`} color="bg-red-700" />
        <StatCard icon={FolderOpen} label="Projects" value={data.projects.total}
          sub="In customer bank" color="bg-purple-600" />
        {!isEngineering && (
          <StatCard icon={Handshake} label="Total Deals" value={data.deals.total}
            sub="All stages" color="bg-amber-500" />
        )}
        {isFinance && (
          <>
            <StatCard icon={Wallet} label="BR Approved" value={formatMMK(data.finance.br_approved)}
              sub={`of ${formatMMK(data.finance.br_total)} total`} color="bg-green-600" />
            <StatCard icon={Wallet} label="EC Approved" value={formatMMK(data.finance.ec_approved)}
              sub={`of ${formatMMK(data.finance.ec_total)} total`} color="bg-teal-600" />
            <StatCard icon={Wallet} label="AC Approved" value={formatMMK(data.finance.ac_approved)}
              sub={`of ${formatMMK(data.finance.ac_total)} total`} color="bg-indigo-600" />
          </>
        )}
        {!isFinance && !isEngineering && (
          <StatCard icon={Users} label="Open SRs"
            value={data.sr.by_status.find(s => s.name === 'Open')?.value || 0}
            sub="Needs attention" color="bg-rose-500" />
        )}
      </div>

      {/* SR Trend + Workload */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Service Request Trend (Last 6 Months)">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.sr.trend} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94A3B8' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} allowDecimals={false} />
              <Tooltip content={<CustomBarTooltip />} />
              <Line type="monotone" dataKey="count" name="SRs" stroke="#7F1D1D" strokeWidth={2.5} dot={{ fill: '#7F1D1D', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Team Workload — Open SRs by Assignee">
          {data.workload.length === 0 ? (
            <div className="flex items-center justify-center h-[200px] text-sm text-slate-400">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.workload.slice(0, 8)} layout="vertical" margin={{ top: 5, right: 10, left: 60, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#64748B' }} width={60} />
                <Tooltip content={<CustomBarTooltip />} />
                <Bar dataKey="open" name="Open" fill="#EF4444" radius={[0, 3, 3, 0]} />
                <Bar dataKey="total" name="Total" fill="#7F1D1D" radius={[0, 3, 3, 0]} opacity={0.4} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* SR Breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <ChartCard title="SR by Status">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={data.sr.by_status} cx="50%" cy="50%" outerRadius={70} dataKey="value" nameKey="name" label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                {data.sr.by_status.map((_, i) => (
                  <Cell key={i} fill={srStatusColors[i]} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="SR by Priority">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={data.sr.by_priority} cx="50%" cy="50%" outerRadius={70} dataKey="value" nameKey="name" label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                {data.sr.by_priority.map((_, i) => (
                  <Cell key={i} fill={prioColors[i % prioColors.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="SR by Category">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.sr.by_category} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#94A3B8' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} allowDecimals={false} />
              <Tooltip content={<CustomBarTooltip />} />
              <Bar dataKey="value" name="Count" fill="#7F1D1D" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Projects */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ChartCard title="Projects by Type">
          {data.projects.by_type.length === 0 ? (
            <div className="flex items-center justify-center h-[180px] text-sm text-slate-400">No projects</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={data.projects.by_type} cx="50%" cy="50%" outerRadius={65} dataKey="value" nameKey="name"
                  label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                  {data.projects.by_type.map((_, i) => (
                    <Cell key={i} fill={typeColors[i % typeColors.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {!isEngineering && (
          <ChartCard title="Deal Pipeline — Funnel">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.deals.funnel} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="stage" tick={{ fontSize: 9, fill: '#94A3B8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} allowDecimals={false} />
                <Tooltip content={<CustomBarTooltip />} />
                <Bar dataKey="count" name="Deals" radius={[3, 3, 0, 0]}>
                  {data.deals.funnel.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.stage] || PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
        {isEngineering && (
          <ChartCard title="Projects by Status">
            {data.projects.by_status.length === 0 ? (
              <div className="flex items-center justify-center h-[180px] text-sm text-slate-400">No projects</div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data.projects.by_status} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94A3B8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} allowDecimals={false} />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Bar dataKey="value" name="Projects" fill="#7F1D1D" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        )}
      </div>

      {/* Finance section — admin/finance only */}
      {isFinance && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <ChartCard title="Budget Requests by Status">
            <ResponsiveContainer width="100%" height={170}>
              <PieChart>
                <Pie data={data.finance.br_by_status} cx="50%" cy="50%" outerRadius={60} dataKey="value" nameKey="name"
                  label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                  {data.finance.br_by_status.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.name] || PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Expense Claims by Status">
            <ResponsiveContainer width="100%" height={170}>
              <PieChart>
                <Pie data={data.finance.ec_by_status} cx="50%" cy="50%" outerRadius={60} dataKey="value" nameKey="name"
                  label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                  {data.finance.ec_by_status.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.name] || PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Finance Summary (MMK)">
            <div className="space-y-3 py-2">
              {[
                { label: 'BR Total Requested', value: data.finance.br_total },
                { label: 'BR Total Approved', value: data.finance.br_approved },
                { label: 'EC Total Requested', value: data.finance.ec_total },
                { label: 'EC Total Approved', value: data.finance.ec_approved },
                { label: 'AC Total Approved', value: data.finance.ac_approved },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">{label}</span>
                  <span className="text-sm font-semibold text-slate-900">{formatMMK(value)}</span>
                </div>
              ))}
            </div>
          </ChartCard>
        </div>
      )}
    </div>
  )
}
